

import { useState, useEffect } from "react";
import { X } from "lucide-react";
import Badge from "../components/Badge";
import { enquiriesApi, mastersApi } from "@/lib/api";
import { useToast } from "@/context/ToastContext";
import { formatDate } from "@/lib/data";

interface Enquiry {
  id: number;
  name: string;
  phone: string;
  email: string;
  event_date: string;
  guests: number;
  event_type: string;
  status: string;
  notes: string;
  lead_source: string;
  created_at: string;
  client_id?: number;
  package_id?: number;
  estimated_budget?: number;
  client?: { id: number; name: string };
}

interface EnquiryDetailModalProps {
  isOpen: boolean;
  enquiryId: number | null;
  onClose: () => void;
  onEnquiryUpdated?: () => void;
}

export default function EnquiryDetailModal({
  isOpen,
  enquiryId,
  onClose,
  onEnquiryUpdated
}: EnquiryDetailModalProps) {
  const { showToast } = useToast();

  const [enquiry, setEnquiry] = useState<Enquiry | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingField, setEditingField] = useState<string | null>(null);
  const [editValue, setEditValue] = useState<string>("");
  const [eventTypes, setEventTypes] = useState<string[]>([]);
  const [showConvertModal, setShowConvertModal] = useState(false);

  useEffect(() => {
    if (!isOpen || !enquiryId) {
      setEnquiry(null);
      return;
    }

    const loadData = async () => {
      try {
        setLoading(true);
        const [enquiryRes, typesRes] = await Promise.all([
          enquiriesApi.get(enquiryId),
          mastersApi.eventTypes.list({ status: "active" })
        ]);

        setEnquiry(enquiryRes.data?.data || enquiryRes.data);
        const types = typesRes.data?.data || typesRes.data || [];
        setEventTypes(types.map((t: any) => t.name));
      } catch (error) {
        showToast("Failed to load enquiry", "error");
        setEnquiry(null);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [isOpen, enquiryId, showToast]);

  const handleUpdateField = async (field: string, value: any) => {
    if (!enquiry) return;
    setSaving(true);
    try {
      const updateData: any = {};
      if (field === "name") updateData.client_name = value;
      else if (field === "phone") updateData.client_phone = value;
      else if (field === "email") updateData.client_email = value;
      else if (field === "event_date") updateData.event_date = value;
      else if (field === "event_type") updateData.type = value;
      else if (field === "guests") updateData.guest_count = parseInt(value) || 0;
      else if (field === "status") updateData.status = value;
      else updateData[field] = value;

      await enquiriesApi.update(enquiry.id, updateData);
      setEnquiry({ ...enquiry, [field]: value });
      setEditingField(null);
      showToast("Updated successfully", "success");
      onEnquiryUpdated?.();
    } catch (error) {
      showToast("Failed to update", "error");
    } finally {
      setSaving(false);
    }
  };

  const handleStatusChange = async (newStatus: string) => {
    if (!enquiry) return;
    setSaving(true);
    try {
      await enquiriesApi.update(enquiry.id, { status: newStatus });
      setEnquiry({ ...enquiry, status: newStatus });
      showToast(`Status updated to ${newStatus}`, "success");
      onEnquiryUpdated?.();
    } catch (error) {
      showToast("Failed to update status", "error");
    } finally {
      setSaving(false);
    }
  };

  const handleConvertToEvent = async () => {
    if (!enquiry) return;
    try {
      await enquiriesApi.convert(enquiry.id, {});
      showToast("Enquiry converted to event successfully", "success");
      setShowConvertModal(false);
      onClose();
      onEnquiryUpdated?.();
    } catch (error) {
      showToast("Failed to convert enquiry", "error");
    }
  };

  const handleDelete = async () => {
    if (!enquiry) return;
    if (!confirm("Are you sure you want to delete this enquiry?")) return;
    
    try {
      await enquiriesApi.delete(enquiry.id);
      showToast("Enquiry deleted successfully", "success");
      onClose();
      onEnquiryUpdated?.();
    } catch (error) {
      showToast("Failed to delete enquiry", "error");
    }
  };

  if (!isOpen) return null;

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
        <div className="bg-white rounded-2xl p-8 flex flex-col items-center gap-3 shadow-xl">
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-amber-500 border-t-transparent"></div>
          <span className="text-stone-700 font-medium">Loading enquiry...</span>
        </div>
      </div>
    );
  }

  if (!enquiry) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
        <div className="bg-white rounded-2xl p-8 max-w-md w-full shadow-xl">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold text-stone-800">Enquiry Details</h2>
            <button
              onClick={onClose}
              className="p-1 hover:bg-stone-100 rounded-lg transition-colors"
            >
              <X className="w-5 h-5 text-stone-600" />
            </button>
          </div>
          <p className="text-red-600 font-medium">Enquiry not found</p>
        </div>
      </div>
    );
  }

  const statusActions: Record<string, string[]> = {
    new: ["qualified", "rejected"],
    qualified: ["converted", "rejected"],
    converted: [],
    rejected: ["new", "qualified"],
  };

  const EditableField = ({
    label,
    field,
    value,
    type = "text",
    options,
  }: {
    label: string;
    field: string;
    value: any;
    type?: string;
    options?: { value: string; label: string }[];
  }) => (
    <div className="group relative">
      <label className="text-xs text-stone-400 block mb-1">{label}</label>
      {editingField === field ? (
        <div className="flex gap-2">
          {type === "select" && options ? (
            <select
              value={editValue}
              onChange={(e) => setEditValue(e.target.value)}
              className="flex-1 px-2 py-1.5 rounded-lg border border-amber-300 text-sm focus:border-amber-400 focus:ring-1 focus:ring-amber-200 outline-none"
              autoFocus
            >
              {options.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          ) : (
            <input
              type={type}
              value={editValue}
              onChange={(e) => setEditValue(e.target.value)}
              className="flex-1 px-2 py-1.5 rounded-lg border border-amber-300 text-sm focus:border-amber-400 focus:ring-1 focus:ring-amber-200 outline-none"
              autoFocus
              onKeyDown={(e) => {
                if (e.key === "Enter") handleUpdateField(field, editValue);
                if (e.key === "Escape") setEditingField(null);
              }}
            />
          )}
          <button
            onClick={() => handleUpdateField(field, editValue)}
            disabled={saving}
            className="px-2 py-1 rounded-lg bg-emerald-100 text-emerald-700 text-xs font-medium hover:bg-emerald-200"
          >
            ✓
          </button>
          <button
            onClick={() => setEditingField(null)}
            className="px-2 py-1 rounded-lg bg-stone-100 text-stone-600 text-xs font-medium hover:bg-stone-200"
          >
            ✕
          </button>
        </div>
      ) : (
        <div
          onClick={() => {
            setEditingField(field);
            setEditValue(String(value || ""));
          }}
          className="cursor-pointer hover:bg-amber-50 px-2 py-1.5 rounded-lg border border-transparent hover:border-amber-200 transition-colors"
        >
          <span className="text-sm font-medium text-stone-800">{value || "-"}</span>
          <span className="ml-2 opacity-0 group-hover:opacity-100 text-amber-500 text-xs">✎</span>
        </div>
      )}
    </div>
  );

  return (
    <>
      <div className={`fixed inset-0 bg-black/50 z-50 overflow-y-auto ${showConvertModal ? 'hidden' : ''}`}>
        <div className="min-h-screen flex flex-col">
          {/* Header */}
          <div className="bg-white border-b border-stone-200 sticky top-0 z-10">
            <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
              <h1 className="text-2xl font-bold text-stone-800">{enquiry.name}</h1>
              <button
                onClick={onClose}
                className="p-2 hover:bg-stone-100 rounded-lg transition-colors"
              >
                <X className="w-6 h-6 text-stone-600" />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 bg-stone-50 overflow-y-auto">
            <div className="max-w-7xl mx-auto px-4 py-6 space-y-4">
          {/* Header Info */}
          <div className="flex items-start justify-between pb-4 border-b border-stone-200">
            <div>
              <p className="text-xs text-stone-500">#{enquiry.id} • Created {enquiry.created_at ? formatDate(enquiry.created_at) : "-"}</p>
              <Badge label={enquiry.status} status={enquiry.status} />
            </div>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="text-xs font-semibold text-stone-500 uppercase tracking-wide mb-3">Contact</h4>
            <div className="space-y-2">
              <EditableField label="Phone" field="phone" value={enquiry.phone} type="tel" />
              <EditableField label="Email" field="email" value={enquiry.email} type="email" />
            </div>
          </div>

          {/* Event Details */}
          <div>
            <h4 className="text-xs font-semibold text-stone-500 uppercase tracking-wide mb-3">Event</h4>
            <div className="space-y-2">
              <EditableField
                label="Type"
                field="event_type"
                value={enquiry.event_type}
                type="select"
                options={eventTypes.map((t) => ({ value: t, label: t }))}
              />
              <EditableField label="Date" field="event_date" value={enquiry.event_date} type="date" />
              <EditableField label="Guests" field="guests" value={enquiry.guests} type="number" />
            </div>
          </div>

          {/* Source & Budget */}
          <div>
            <h4 className="text-xs font-semibold text-stone-500 uppercase tracking-wide mb-3">Source</h4>
            <div className="space-y-2">
              <div>
                <label className="text-xs text-stone-400 block mb-1">Lead Source</label>
                <span className="text-sm font-medium text-stone-800">{enquiry.lead_source || "-"}</span>
              </div>
              <div>
                <label className="text-xs text-stone-400 block mb-1">Client</label>
                <span className="text-sm font-medium text-stone-800">{enquiry.client?.name || enquiry.name}</span>
              </div>
            </div>
          </div>

          {/* Status Actions */}
          {statusActions[enquiry.status]?.length > 0 && (
            <div className="bg-stone-50 rounded-lg p-3">
              <p className="text-xs text-stone-500 mb-2">Update Status:</p>
              <div className="flex flex-wrap gap-2">
                {statusActions[enquiry.status].map((action) => (
                  <button
                    key={action}
                    onClick={() => handleStatusChange(action)}
                    disabled={saving}
                    className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${
                      action === "converted"
                        ? "bg-emerald-100 text-emerald-700 hover:bg-emerald-200"
                        : action === "qualified"
                        ? "bg-violet-100 text-violet-700 hover:bg-violet-200"
                        : action === "rejected"
                        ? "bg-rose-100 text-rose-700 hover:bg-rose-200"
                        : "bg-stone-100 text-stone-700 hover:bg-stone-200"
                    }`}
                  >
                    {action.charAt(0).toUpperCase() + action.slice(1)}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Notes */}
          {enquiry.notes && (
            <div className="bg-stone-50 rounded-lg p-3">
              <label className="text-xs text-stone-400 block mb-1">Notes</label>
              <p className="text-sm text-stone-600">{enquiry.notes}</p>
            </div>
          )}

          {/* Quick Actions */}
          <div className="flex gap-2 pt-4 border-t border-stone-200">
            {enquiry.status !== "converted" && enquiry.status !== "rejected" && (
              <button
                onClick={() => setShowConvertModal(true)}
                className="flex-1 px-3 py-2 rounded-lg bg-amber-100 text-amber-700 text-xs font-semibold hover:bg-amber-200 transition-colors"
              >
                Convert to Event
              </button>
            )}
            <button
              onClick={handleDelete}
              className="flex-1 px-3 py-2 rounded-lg bg-rose-100 text-rose-700 text-xs font-semibold hover:bg-rose-200 transition-colors"
            >
              Delete
            </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Convert Modal */}
      {showConvertModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-8 max-w-md w-full shadow-xl">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-stone-800">Convert to Event</h2>
              <button
                onClick={() => setShowConvertModal(false)}
                className="p-1 hover:bg-stone-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-stone-600" />
              </button>
            </div>
            <div className="space-y-4">
              <p className="text-sm text-stone-600">
                This will convert the enquiry to an event. The enquiry status will be updated to "converted".
              </p>
              <div className="bg-stone-50 rounded-xl p-4">
                <div className="text-xs text-stone-400 mb-1">Client</div>
                <div className="font-semibold text-stone-800">{enquiry.name}</div>
                <div className="text-xs text-stone-500 mt-2">{enquiry.event_type} • {enquiry.event_date}</div>
              </div>
              <div className="flex gap-3">
                <button
                  onClick={() => setShowConvertModal(false)}
                  className="flex-1 px-4 py-2.5 rounded-xl bg-stone-100 text-stone-600 font-semibold text-sm"
                >
                  Cancel
                </button>
                <button
                  onClick={handleConvertToEvent}
                  className="flex-1 px-4 py-2.5 rounded-xl text-white font-semibold text-sm gold-gradient"
                >
                  Convert
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
